// File: frontend/src/pages/UPS/UPS.js

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { categoryService } from '../../services/categoryService';
import styles from './UPS.module.css';

const UPS = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('browse');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Browse Categories Tab
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryValues, setCategoryValues] = useState([]);
  
  // Test Validation Tab
  const [testCategory, setTestCategory] = useState('');
  const [testValue, setTestValue] = useState('');
  const [validationResult, setValidationResult] = useState(null);
  const [availableKeys, setAvailableKeys] = useState([]);

  // Load all categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.getAllCategoryTypes();
      setCategories(response.categoryTypes || []);
    } catch (err) {
      setError(`Failed to load categories: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = async (category) => {
    try {
      setLoading(true);
      setError(null);
      setSelectedCategory(category);
      
      const response = await categoryService.getCategoryValues(category.category_type_id);
      setCategoryValues(response.categoryValues || []);
    } catch (err) {
      setError(`Failed to load category values: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleValidationTest = async () => {
    if (!testCategory.trim() || !testValue.trim()) {
      setError('Please enter both category name and value to test');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get validation result
      const validation = await categoryService.validateValue(testCategory, testValue);
      setValidationResult(validation);
      
      // Also get available keys for reference
      const keys = await categoryService.getValueKeys(testCategory);
      setAvailableKeys(keys.valueKeys || []);
    } catch (err) {
      setError(`Validation failed: ${err.message}`);
      setValidationResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGetValueKeys = async () => {
    if (!testCategory.trim()) {
      setError('Please enter a category name');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const keys = await categoryService.getValueKeys(testCategory);
      setAvailableKeys(keys.valueKeys || []);
      setValidationResult(null);
    } catch (err) {
      setError(`Failed to get value keys: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = categories.filter(cat => 
    cat.category_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cat.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.upsPage}>
      <div className={styles.header}>
        <h1 className={styles.title}>Category API Testing</h1>
        <p className={styles.subtitle}>Test and explore category management APIs</p>
      </div>

      {/* Navigation Tabs */}
      <div className={styles.navigation}>
        <button 
          className={`${styles.navButton} ${activeTab === 'browse' ? styles.active : ''}`}
          onClick={() => setActiveTab('browse')}
        >
          Browse Categories
        </button>
        <button 
          className={`${styles.navButton} ${activeTab === 'validate' ? styles.active : ''}`}
          onClick={() => setActiveTab('validate')}
        >
          Test Validation
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className={styles.error}>
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Tab Content */}
      <div className={styles.content}>
        {activeTab === 'browse' && (
          <div className={styles.browseTab}>
            <div className={styles.categoriesSection}>
              <div className={styles.sectionHeader}>
                <h2>Available Categories ({categories.length})</h2>
                <div className={styles.searchBox}>
                  <input
                    type="text"
                    placeholder="Search categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                  />
                </div>
              </div>
              
              <div className={styles.categoriesList}>
                {loading ? (
                  <div className={styles.loading}>Loading categories...</div>
                ) : filteredCategories.length === 0 ? (
                  <div className={styles.empty}>No categories found</div>
                ) : (
                  filteredCategories.map((category) => (
                    <div 
                      key={category.category_type_id}
                      className={`${styles.categoryCard} ${
                        selectedCategory?.category_type_id === category.category_type_id ? styles.selected : ''
                      }`}
                      onClick={() => handleCategorySelect(category)}
                    >
                      <div className={styles.categoryHeader}>
                        <h3>{category.category_name}</h3>
                        <span className={styles.categoryId}>ID: {category.category_type_id}</span>
                      </div>
                      {category.description && (
                        <p className={styles.categoryDescription}>{category.description}</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {selectedCategory && (
              <div className={styles.valuesSection}>
                <div className={styles.sectionHeader}>
                  <h2>Values for "{selectedCategory.category_name}"</h2>
                  <span className={styles.count}>({categoryValues.length} values)</span>
                </div>
                
                <div className={styles.valuesList}>
                  {loading ? (
                    <div className={styles.loading}>Loading values...</div>
                  ) : categoryValues.length === 0 ? (
                    <div className={styles.empty}>No values found</div>
                  ) : (
                    categoryValues.map((value) => (
                      <div key={value.category_value_id} className={styles.valueCard}>
                        <div className={styles.valueHeader}>
                          <span className={styles.valueKey}>{value.value_key}</span>
                          <span className={`${styles.status} ${
                            value.is_active === 'true' ? styles.active : styles.inactive
                          }`}>
                            {value.is_active === 'true' ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {value.description && (
                          <p className={styles.valueDescription}>{value.description}</p>
                        )}
                        <div className={styles.valueId}>ID: {value.category_value_id}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'validate' && (
          <div className={styles.validateTab}>
            <div className={styles.testSection}>
              <h2>Test Category Validation</h2>
              
              <div className={styles.formGroup}>
                <label>Category Name:</label>
                <input
                  type="text"
                  placeholder="e.g., country_code, user_type, account_status"
                  value={testCategory}
                  onChange={(e) => setTestCategory(e.target.value)}
                  className={styles.input}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Value to Test:</label>
                <input
                  type="text"
                  placeholder="e.g., ca, admin, active"
                  value={testValue}
                  onChange={(e) => setTestValue(e.target.value)}
                  className={styles.input}
                />
              </div>

              <div className={styles.buttonGroup}>
                <button 
                  onClick={handleValidationTest}
                  disabled={loading}
                  className={styles.primaryButton}
                >
                  {loading ? 'Testing...' : 'Validate Value'}
                </button>
                <button 
                  onClick={handleGetValueKeys}
                  disabled={loading}
                  className={styles.secondaryButton}
                >
                  {loading ? 'Loading...' : 'Get Valid Keys'}
                </button>
              </div>

              {/* Validation Results */}
              {validationResult && (
                <div className={styles.results}>
                  <h3>Validation Result</h3>
                  <div className={`${styles.resultCard} ${
                    validationResult.isValid ? styles.valid : styles.invalid
                  }`}>
                    <div className={styles.resultHeader}>
                      <span className={styles.resultStatus}>
                        {validationResult.isValid ? '✅ Valid' : '❌ Invalid'}
                      </span>
                    </div>
                    <div className={styles.resultDetails}>
                      <p><strong>Category:</strong> {validationResult.categoryName}</p>
                      <p><strong>Value:</strong> {validationResult.valueKey}</p>
                      {validationResult.error && (
                        <p className={styles.error}><strong>Error:</strong> {validationResult.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Available Keys */}
              {availableKeys.length > 0 && (
                <div className={styles.results}>
                  <h3>Available Keys for "{testCategory}" ({availableKeys.length})</h3>
                  <div className={styles.keysList}>
                    {availableKeys.map((key, index) => (
                      <span 
                        key={index} 
                        className={styles.keyTag}
                        onClick={() => setTestValue(key)}
                        title="Click to test this value"
                      >
                        {key}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UPS;